/**
 * menu-config.ts — the ONE list behind the menu (Phase A of the menu
 * restructure, Gordon 2026-11-03: "Börja etapp A").
 *
 * The sidebar (desktop + phone sheet), the phone pull-up tray, Settings →
 * Layout, the Super Admin default-menu editor, coπ's page names and the auth
 * gate's public pages all read this file, so they can no longer drift apart.
 * Pure data + pure functions (no browser SDK, no icons) — server code and
 * node tests import it too.
 *
 * Groups (Andrés's menu, 2026-11-03):
 *   explore — always on, the same for visitors and members, can't be hidden:
 *             Home, Marketplace, Sync Licensing, Book Talent, Pro-Launch
 *   mybb    — members only: Dashboard + Profile first (always on), then
 *             their own pages, each on/off, in their own order
 *   create  — members only, ONE "+ Create" entry that opens these shortcuts
 *   more    — members only, off unless switched on
 *   account — members only, the account menu under their avatar: Settings,
 *             (Switch account), Admin + Hermes (super admins), (Download App),
 *             (Sign out). The bracketed ones are behaviour, built in sidebar-nav.
 *   bottom  — pinned to the bottom of the menu, exactly three, the same
 *             order and place signed in or not: Themes, Help, Account
 *             (Sign in for visitors; avatar + name opening the account menu)
 *
 * Saved menus live in users/{uid}/preferences/layout.nav = [{ href, visible,
 * order }] (href = the item's `id`). Existing members keep theirs untouched;
 * see mergeSavedNav() for how items they've never seen arrive.
 */

export type MenuGroup = 'explore' | 'mybb' | 'create' | 'more' | 'end' | 'account' | 'bottom';

/** Icon names — sidebar-nav / mobile-footer-nav map them to components. */
export type MenuIcon =
  | 'home' | 'dashboard' | 'story' | 'marketplace' | 'sync' | 'launch'
  | 'profile' | 'media' | 'studio' | 'money' | 'academy' | 'collab' | 'community'
  | 'feed' | 'events' | 'news' | 'broadcast' | 'wishlist' | 'stats' | 'bookTalent'
  | 'creatorAi' | 'legalAi' | 'spread' | 'settings'
  | 'upload' | 'memo' | 'songIdea' | 'cover' | 'snap' | 'epx' | 'create'
  | 'supporters' | 'transparency' | 'whyBb' | 'charts'
  | 'themes' | 'help' | 'admin' | 'hermes' | 'account';

export interface MenuItem {
  /** Unique key. For anything with an on/off switch it is ALSO the saved
   *  pref key (`nav[].href`) — pages use their path, EP-X keeps its old
   *  external URL so existing saved switches still apply. */
  id: string;
  /** Where a tap goes (omitted for event actions). */
  href?: string;
  /** `nav.*` translation key for the visible name; brand names have none. */
  navKey?: string;
  /** English name: coπ's page names, admin lists, the fallback label. */
  label: string;
  icon: MenuIcon;
  group: MenuGroup;
  /** The PAGE needs an account (visitors are sent to sign in). Who sees the
   *  item in the menu is decided by its group: visitors get Explore + Bottom. */
  auth: boolean;
  /** Always on — can never be switched off (Home, Dashboard, Profile,
   *  Settings: CORE_NAV_HREFS). In My BB the locked ones are pinned first. */
  locked?: boolean;
  /** A fixed "+ Create" shortcut with no switch of its own. */
  fixed?: boolean;
  /** Kept as data (it filters nothing today). */
  creatorOnly?: boolean;
  /** Page-visibility key for Super Admin's role rules (visibility-context);
   *  defaults to the path without its leading slash. */
  pageKey?: string;
  /** Opens something by window event instead of a page. */
  event?: { name: string; detail?: unknown };
  /** Extra English names coπ understands ("go to platform"). */
  speech?: string[];
  /** Bottom row items only super admins see. */
  superAdmin?: boolean;
}

export const EPX_PREF_ID = 'https://ep.breakinbread.com';

export const MENU_ITEMS: readonly MenuItem[] = [
  // ── Explore: the same for everyone, always on (Andrés's menu, 2026-11-03) ─
  { id: '/home',            href: '/home',            navKey: 'home',        label: 'Home',           icon: 'home',        group: 'explore', auth: false, locked: true },
  { id: '/marketplace',     href: '/marketplace',     navKey: 'marketplace', label: 'Marketplace',    icon: 'marketplace', group: 'explore', auth: false },
  // Public since 2026-09-11 — the catalogue is a storefront for outside buyers.
  { id: '/synchronization', href: '/synchronization', navKey: 'sync',        label: 'Sync Licensing', icon: 'sync',        group: 'explore', auth: false, creatorOnly: true },
  // Open to visitors — anyone can book (Gordon 2026-11-03).
  { id: '/book',            href: '/book',            navKey: 'bookTalent',  label: 'Book Talent',    icon: 'bookTalent',  group: 'explore', auth: false },
  // The 13 Nov pro-launch — last in Explore (Gordon 2026-10-31).

  // ── My BB: members. Dashboard + Profile first, always on; the rest each
  //    switchable, in the member's own order ───────────────────────────────
  // The page itself stays public (visitors reach its public tabs by links).
  { id: '/dashboard',       href: '/dashboard',       navKey: 'dashboard',   label: 'Dashboard',      icon: 'dashboard',   group: 'mybb', auth: false, locked: true },
  { id: '/profile',         href: '/profile',         navKey: 'profile',     label: 'Profile',        icon: 'profile',     group: 'mybb', auth: true, locked: true },
  { id: '/media',           href: '/media',           navKey: 'media',       label: 'Media Vault',    icon: 'media',       group: 'mybb', auth: true, creatorOnly: true },
  // Studio hub — Artwork / Film / DJ / Recorder / Songwriter / Live / Podcast.
  { id: '/artwork-studio',  href: '/artwork-studio',  navKey: 'studio',      label: 'Studio',         icon: 'studio',      group: 'mybb', auth: true, creatorOnly: true },
  // Money hub — Wallet / Earnings / Bookkeeping / Publishing.
  { id: '/wallet',          href: '/wallet',          navKey: 'money',       label: 'Money',          icon: 'money',       group: 'mybb', auth: true },
  { id: '/academy',         href: '/academy',         navKey: 'academy',     label: 'BB Academy',     icon: 'academy',     group: 'mybb', auth: true },
  { id: '/collab',          href: '/collab',          navKey: 'collab',      label: 'Collab',         icon: 'collab',      group: 'mybb', auth: true },
  // Community hub — Wall / Jam Session / Listening Party.
  { id: '/community',       href: '/community',       navKey: 'community',   label: 'Community',      icon: 'community',   group: 'mybb', auth: true },
  { id: '/feed',            href: '/feed',            navKey: 'feed',        label: 'Feed',           icon: 'feed',        group: 'mybb', auth: true },
  { id: '/events',          href: '/events',          navKey: 'events',      label: 'Events',         icon: 'events',      group: 'mybb', auth: true },
  { id: '/news',            href: '/news',            navKey: 'news',        label: 'News',           icon: 'news',        group: 'mybb', auth: true },
  // Broadcast hub — Live / Podcasts / Mixtapes / Record Show.
  { id: '/radio',           href: '/radio',           navKey: 'broadcast',   label: 'Broadcast',      icon: 'broadcast',   group: 'mybb', auth: true },
  { id: '/wishlist',        href: '/wishlist',        navKey: 'wishlist',    label: 'Wishlist',       icon: 'wishlist',    group: 'mybb', auth: true },
  { id: '/stats',           href: '/stats',           navKey: 'stats',       label: 'Stats',          icon: 'stats',       group: 'mybb', auth: true },
  { id: '/creator-ai',      href: '/creator-ai',      navKey: 'creatorAi',   label: 'Creator AI',     icon: 'creatorAi',   group: 'mybb', auth: true },
  { id: '/legal-ai',        href: '/legal-ai',        navKey: 'legalAi',     label: 'Legal AI',       icon: 'legalAi',     group: 'mybb', auth: true },
  { id: '/spread',          href: '/spread',          navKey: 'spread',      label: 'Spread',         icon: 'spread',      group: 'mybb', auth: true },

  // ── + Create: one entry that opens these (what coπ opens by voice) ──────
  { id: 'create-upload', href: '/media?upload=single',      navKey: 'quickUpload',   label: 'Upload music',  icon: 'upload',   group: 'create', auth: true, fixed: true, pageKey: 'media' },
  { id: 'create-memo',   href: '/studio/recorder?ready=1',  navKey: 'quickMemo',     label: 'Memo',          icon: 'memo',     group: 'create', auth: true, fixed: true },
  { id: 'create-song',   href: '/studio/songwriter',        navKey: 'quickSongIdea', label: 'Song idea',     icon: 'songIdea', group: 'create', auth: true, fixed: true },
  { id: 'create-cover',  href: '/artwork-studio',           navKey: 'quickCover',    label: 'Make a cover',  icon: 'cover',    group: 'create', auth: true, fixed: true },
  { id: 'create-snap',   event: { name: 'bb-snap', detail: { kind: 'receipt' } }, navKey: 'quickSnap', label: 'Snap receipt', icon: 'snap', group: 'create', auth: true, fixed: true },
  // EP-X opens INSIDE BB (Studio → EP-X); it keeps its own switch.
  { id: EPX_PREF_ID,     href: '/studio/epx',                                        label: 'EP-X',          icon: 'epx',      group: 'create', auth: true },

  // ── More: off unless switched on (these were Dashboard-tab pages) ──────
  { id: '/supporters',      href: '/supporters',      navKey: 'supporters',   label: 'Angels',       icon: 'supporters',   group: 'more', auth: false },
  // Opens Dashboard → Transparency, so it's called that now (was "Platform").
  { id: '/platform',        href: '/platform',        navKey: 'transparency', label: 'Transparency', icon: 'transparency', group: 'more', auth: false, speech: ['Platform', 'Platform stats'] },
  { id: '/why-bb',          href: '/why-bb',          navKey: 'whyBb',        label: 'Why BB',       icon: 'whyBb',        group: 'more', auth: false },
  // The animated Why BB story — was in Explore until Andrés's menu (2026-11-03).
  // ── End: last in the menu, just above the bottom row, for everyone (Gordon 2026-11-03:
  //    "Beta demo can go at the bottom of the menu together with the Why BB story") ──
  { id: '/why-bb/story',    href: '/why-bb/story',    navKey: 'story',        label: 'BB story',     icon: 'story',        group: 'end', auth: false },
  { id: '/launch',          href: '/launch',          navKey: 'launch',       label: 'Beta demo',    icon: 'launch',       group: 'end', auth: false },
  // From the old phone tray — a page of its own (not a hub tab).
  { id: '/charts',          href: '/charts',          navKey: 'charts',       label: 'Charts',       icon: 'charts',       group: 'more', auth: false },

  // ── Account menu (members): opens from the avatar in the bottom row ────
  // Settings keeps its saved switch (old menus have it) but is no longer a
  // My BB row. Switch account, Download App and Sign out are added in
  // sidebar-nav — they need their own behaviour.
  { id: '/settings',     href: '/settings',     navKey: 'settings', label: 'Settings', icon: 'settings', group: 'account', auth: true, locked: true, creatorOnly: true },
  { id: '/admin',        href: '/admin',        navKey: 'admin',    label: 'Admin',    icon: 'admin',    group: 'account', auth: true, superAdmin: true },
  { id: '/admin/hermes', href: '/admin/hermes',                     label: 'Hermes',   icon: 'hermes',   group: 'account', auth: true, superAdmin: true },

  // ── Bottom: pinned to the bottom of the menu — exactly three, the same
  //    order and place for visitors and members ─────────────────────────
  { id: 'themes',  event: { name: 'bb-open-theme-picker' }, navKey: 'themes',  label: 'Themes',  icon: 'themes',  group: 'bottom', auth: false },
  { id: '/help',   href: '/help',                           navKey: 'help',    label: 'Help',    icon: 'help',    group: 'bottom', auth: false },
  // Visitors: Sign in. Members: their avatar + name → the account menu.
  { id: 'account',                                          navKey: 'account', label: 'Account', icon: 'account', group: 'bottom', auth: false },
];

export const menuGroup = (group: MenuGroup): MenuItem[] => MENU_ITEMS.filter(i => i.group === group);

/** Items with an on/off pref (everything in Explore, My BB, More, EP-X —
 *  and Settings, which saved menus have always held). */
export const PREF_ITEMS: readonly MenuItem[] = MENU_ITEMS.filter(i =>
  i.group === 'explore' || i.group === 'end' || i.group === 'mybb' || i.group === 'more' || i.id === EPX_PREF_ID || i.id === '/settings');
const PREF_IDS = new Set(PREF_ITEMS.map(i => i.id));
const BY_ID = new Map(MENU_ITEMS.map(i => [i.id, i]));
export const menuItem = (id: string): MenuItem | undefined => BY_ID.get(id);

/** Every switchable menu page and its English name — the list Settings,
 *  coπ's nightly review and older importers know as ALL_NAV_ITEMS. */
export const ALL_NAV_ITEMS: { href: string; label: string }[] = PREF_ITEMS.map(i => ({ href: i.id, label: i.label }));

/** Always on for members — Home, Dashboard, Profile, Settings. */
export const CORE_NAV_HREFS: ReadonlySet<string> = new Set(MENU_ITEMS.filter(i => i.locked).map(i => i.id));

/** The pages the once-per-account clean-up (navDefaultsVersion 1,
 *  2026-11-01) switches off — frozen, so a page moving into More later
 *  (BB story, 2026-11-03) never changes what that clean-up does. */
export const HIDDEN_BY_DEFAULT_NAV: ReadonlySet<string> = new Set(['/supporters', '/platform', '/why-bb', '/charts']);

/** Menu pages for coπ's voice commands ("go to marketplace"): every page in
 *  Explore, My BB, More and the account menu (not the super-admin ones),
 *  with its English names. */
export const NAV_PAGES: { href: string; navKey: string; auth: boolean; names: string[] }[] = MENU_ITEMS
  .filter(i => (i.group === 'explore' || i.group === 'mybb' || i.group === 'more' || (i.group === 'account' && !i.superAdmin)) && i.href?.startsWith('/'))
  .map(i => ({ href: i.href!, navKey: i.navKey || '', auth: i.auth, names: [i.label, ...(i.speech || [])] }));

/** Paths every signed-out visitor may open, from the same list (the auth
 *  gate imports this so it can never drift from the menu). */
export const PUBLIC_NAV_HREFS: string[] = MENU_ITEMS
  .filter(i => !i.auth && i.href?.startsWith('/') && !i.href.includes('?'))
  .map(i => i.href!);

/* ------------------------------------------------------------------ */
/*  Defaults                                                           */
/* ------------------------------------------------------------------ */

export interface NavPref { href: string; visible: boolean; order: number }

/** Members whose Firebase account was created at or after this moment and
 *  who have no saved menu get the per-account-type default below; everyone
 *  else keeps the menu they had (Phase A release, 2026-11-03). */
export const MENU_V2_SINCE = '2026-11-03T16:00:00Z';
/** Saved with a menu made from the per-type defaults. */
export const MENU_VERSION = 2;

export function isNewMenuMember(creationTime?: string | null): boolean {
  if (!creationTime) return false;
  const t = Date.parse(creationTime);
  return Number.isFinite(t) && t >= Date.parse(MENU_V2_SINCE);
}

export type MenuAccountKind = 'fan' | 'maker' | 'venue' | 'business' | 'admin';

export function menuAccountKind(accountType?: string | null): MenuAccountKind {
  const t = (accountType || '').toLowerCase();
  if (t === 'superadmin' || t === 'admin') return 'admin';
  if (t === 'creator' || t === 'content_creator' || t === 'artist' || t === 'professional') return 'maker';
  if (t === 'venue') return 'venue';
  if (t === 'consumer-business') return 'business';
  return 'fan';
}

/** What a NEW member's My BB / More / EP-X start with, per account type
 *  (plan §7). Listed order = their starting order; admins get everything.
 *  Explore is always on and isn't listed. Super Admin can override all of
 *  this with one saved default (siteSettings/navDefaults). */
export const MENU_V2_DEFAULTS: Record<Exclude<MenuAccountKind, 'admin'>, readonly string[]> = {
  fan:      ['/profile', '/wishlist', '/feed', '/community', '/events', '/academy', '/settings', EPX_PREF_ID],
  maker:    ['/profile', '/media', '/artwork-studio', '/wallet', '/academy', '/collab', '/creator-ai', '/settings', EPX_PREF_ID],
  venue:    ['/profile', '/events', '/book', '/wallet', '/settings', EPX_PREF_ID],
  business: ['/profile', '/wallet', '/book', '/wishlist', '/settings', EPX_PREF_ID],
};

function v2OnSet(accountType?: string | null): Set<string> | 'all' {
  const kind = menuAccountKind(accountType);
  return kind === 'admin' ? 'all' : new Set(MENU_V2_DEFAULTS[kind]);
}

/** A new member's menu (menuVersion 2). */
export function newMemberNav(accountType?: string | null): NavPref[] {
  const kind = menuAccountKind(accountType);
  const explore = PREF_ITEMS.filter(i => i.group === 'explore').map(i => i.id);
  const listed = kind === 'admin' ? [] : [...MENU_V2_DEFAULTS[kind]];
  const ordered = [...explore, ...listed, ...PREF_ITEMS.map(i => i.id).filter(id => !explore.includes(id) && !listed.includes(id))];
  const on = v2OnSet(accountType);
  return ordered.map((id, order) => ({
    href: id,
    visible: menuItem(id)?.group === 'explore' || !!menuItem(id)?.locked || on === 'all' || on.has(id),
    order,
  }));
}

/** The menu list before Phase A, in its old order — frozen. Collab was
 *  always shown to members then (it had no switch), so it counts as on. */
const MENU_V1_HREFS: readonly string[] = [
  '/home', '/dashboard', '/why-bb/story', '/profile', '/marketplace', '/book', '/supporters', '/community',
  '/feed', '/events', '/news', '/radio', '/platform', '/why-bb', '/wishlist', '/synchronization', '/stats',
  '/academy', '/media', '/artwork-studio', '/wallet', '/creator-ai', '/legal-ai', '/spread', EPX_PREF_ID,
  '/settings', '/launch', '/collab',
];
const MENU_V1 = new Set(MENU_V1_HREFS);
const V1_MORE = new Set(['/supporters', '/platform', '/why-bb']);
const V1_MINIMAL = new Set(['/home', '/dashboard', '/profile', '/settings', '/launch', '/collab']);

/** The menu a member who never saved one has always had (before Phase A):
 *  admins everything but the More pages, everyone else the minimal set.
 *  Items added since then are off. */
export function legacyDefaultNav(accountType?: string | null): NavPref[] {
  const admin = menuAccountKind(accountType) === 'admin';
  const ordered = [...MENU_V1_HREFS, ...PREF_ITEMS.map(i => i.id).filter(id => !MENU_V1.has(id))];
  return ordered.map((id, order) => ({
    href: id,
    visible: MENU_V1.has(id) && !V1_MORE.has(id) && (admin || V1_MINIMAL.has(id)),
    order,
  }));
}

/**
 * A saved menu brought up to date with the current list:
 *  - unknown hrefs (merged hubs, removed pages) are dropped;
 *  - items it has never seen are added at the end:
 *      menuVersion 2 → on if their account type's default has them;
 *      older menus   → on only if they existed before Phase A (so nobody's
 *                      menu changes) — anything added later arrives OFF.
 */
export function mergeSavedNav(
  saved: readonly NavPref[] | undefined | null,
  opts: { menuVersion?: number; accountType?: string | null } = {},
): NavPref[] {
  const kept = (Array.isArray(saved) ? saved : []).filter(n => n && PREF_IDS.has(n.href));
  const seen = new Set(kept.map(n => n.href));
  const merged = kept.map(n => ({ href: n.href, visible: !!n.visible, order: typeof n.order === 'number' ? n.order : 0 }));
  const v2 = opts.menuVersion === MENU_VERSION;
  const on = v2 ? v2OnSet(opts.accountType) : null;
  for (const item of PREF_ITEMS) {
    if (seen.has(item.id)) continue;
    const visible = v2
      ? item.group === 'explore' || item.group === 'end' || !!item.locked || on === 'all' || !!on?.has(item.id)
      : MENU_V1.has(item.id);
    merged.push({ href: item.id, visible, order: merged.length });
  }
  return merged.sort((a, b) => a.order - b.order);
}

/** Super Admin's one saved default, completed: Explore and the always-on
 *  items on, anything else the admin hasn't seen yet off. */
export function completeAdminDefault(saved: readonly NavPref[]): NavPref[] {
  const kept = saved.filter(n => n && PREF_IDS.has(n.href)).map(n => ({ href: n.href, visible: !!n.visible, order: n.order }));
  const seen = new Set(kept.map(n => n.href));
  for (const item of PREF_ITEMS) {
    if (!seen.has(item.id)) kept.push({ href: item.id, visible: item.group === 'explore' || item.group === 'end' || !!item.locked, order: kept.length });
  }
  return kept.sort((a, b) => a.order - b.order);
}

/* ------------------------------------------------------------------ */
/*  What the menu shows                                                */
/* ------------------------------------------------------------------ */

export interface BuiltMenu {
  explore: MenuItem[];
  /** Dashboard + Profile first (pinned), then the member's own order. */
  mybb: MenuItem[];
  create: MenuItem[];
  more: MenuItem[];
  /** Last in the menu, above the bottom row, for everyone: BB story, Beta demo. */
  end: MenuItem[];
  /** The account menu's pages (members only): Settings, then Admin + Hermes
   *  for super admins. */
  account: MenuItem[];
  /** Always exactly Themes, Help, Account. */
  bottom: MenuItem[];
}

/** The key Super Admin's page-visibility rules use for an item. */
export const menuPageKey = (i: MenuItem): string => i.pageKey ?? (i.href || i.id).replace(/^\//, '').split('?')[0];
const pageKeyOf = menuPageKey;

/** Who gets Admin + Hermes in the account menu: the super-admin email
 *  whitelist, a superadmin role, or the superadmin account type. */
const SUPER_ADMIN_EMAILS = ['breakinbreadswe@gmail.com'];
export function isMenuSuperAdmin(email?: string | null, roles?: readonly string[] | null, accountType?: string | null): boolean {
  if (SUPER_ADMIN_EMAILS.includes((email || '').toLowerCase().trim())) return true;
  if ((roles || []).some(r => { const x = String(r).toLowerCase(); return x === 'superadmin' || x === 'super admin'; })) return true;
  return (accountType || '').toLowerCase() === 'superadmin';
}

/** My BB items pinned at the top (always on, can't be dragged). */
export const isPinnedMyBb = (i: MenuItem): boolean => i.group === 'mybb' && !!i.locked;

/**
 * The menu for one person. Visitors: Explore + Bottom. Members: Explore +
 * My BB + Create + More (what's on) + the account menu + Bottom — signing in
 * only ADDS items; Explore never follows a saved switch (that was
 * "Marketplace and Sync disappear when I sign in"), and the bottom row is
 * the same three either way.
 */
export function buildMenu(opts: {
  signedIn: boolean;
  navPrefs: readonly NavPref[];
  isPageVisible?: (pageKey: string) => boolean;
  superAdmin?: boolean;
}): BuiltMenu {
  const { signedIn, navPrefs, superAdmin = false } = opts;
  const pageOk = opts.isPageVisible ?? (() => true);
  const prefs = new Map(navPrefs.map(p => [p.href, p]));
  const on = (i: MenuItem) => !!i.locked || prefs.get(i.id)?.visible === true;
  const byOrder = (a: MenuItem, b: MenuItem) => (prefs.get(a.id)?.order ?? 999) - (prefs.get(b.id)?.order ?? 999);

  const explore = menuGroup('explore');
  const bottom = menuGroup('bottom');
  const end = menuGroup('end');
  if (!signedIn) return { explore, mybb: [], create: [], more: [], end, account: [], bottom };

  const mybb = menuGroup('mybb').filter(i => pageOk(pageKeyOf(i)) && on(i));
  return {
    explore,
    mybb: [...mybb.filter(isPinnedMyBb), ...mybb.filter(i => !isPinnedMyBb(i)).sort(byOrder)],
    create: menuGroup('create').filter(i => (i.fixed ? !i.pageKey || pageOk(i.pageKey) : on(i))),
    more: menuGroup('more').filter(on).sort(byOrder),
    end,
    account: menuGroup('account').filter(i => (i.superAdmin ? superAdmin : pageOk(pageKeyOf(i)))),
    bottom,
  };
}

/**
 * Put `ordered` (some of the menu's prefs, in their new order and with their
 * new switches) back into the full saved list: they take the slots those
 * items had, everything else stays where it was. Orders are restamped.
 */
export function fillSlots(nav: readonly NavPref[], ordered: readonly NavPref[]): NavPref[] {
  const ids = new Set(ordered.map(p => p.href));
  const sorted = [...nav].sort((a, b) => a.order - b.order);
  let next = 0;
  const out = sorted.map(p => (ids.has(p.href) ? ordered[next++] : p));
  while (next < ordered.length) out.push(ordered[next++]);
  return out.map((p, order) => ({ href: p.href, visible: p.visible, order }));
}

/** Drag-reorder inside one group: move the item at `from` to `to` among
 *  `groupIds` (the group as shown), leaving every other item in place. */
export function moveWithinGroup(nav: readonly NavPref[], groupIds: readonly string[], from: number, to: number): NavPref[] {
  if (from === to || from < 0 || to < 0 || from >= groupIds.length || to >= groupIds.length) return [...nav];
  const prefs = new Map(nav.map(p => [p.href, p]));
  const moved = [...groupIds];
  const [m] = moved.splice(from, 1);
  moved.splice(to, 0, m);
  return fillSlots(nav, moved.map(id => prefs.get(id) ?? { href: id, visible: true, order: 0 }));
}
